<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── SEARCH BAR ── -->
      <div class="search-wrap">
        <div class="search-box">
          <span class="search-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input
            v-model="searchText"
            class="search-input"
            type="text"
            :placeholder="tab === 0 ? 'Search a city, business, or category…' : 'Search members by name or title…'"
            @input="handleSearchInput"
          />
          <div class="search-bar-line"></div>
          <button v-if="searchText" class="search-clear" @click="clearSearch">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- ── TABS ── -->
      <div class="tab-row">
        <button class="tab" :class="{ active: tab === 0 }" @click="tab = 0">Places</button>
        <button class="tab" :class="{ active: tab === 1 }" @click="tab = 1">Members</button>
      </div>

      <!-- ── SECTION HEADER ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">{{ tab === 0 ? 'Member Intelligence' : 'The Directory' }}</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">
        <span v-if="searchText">Results for <em>"{{ searchText }}"</em></span>
        <span v-else-if="tab === 0">Search <em>Cities &amp; Places</em></span>
        <span v-else>All <em>Members</em></span>
      </h2>
      <p v-if="tab === 0" class="section-lede">
        Find what fellow members have already recommended in a city — and where they've asked for an official arrangement.
      </p>

      <!-- ══════════════ PLACES TAB ══════════════ -->
      <template v-if="tab === 0">

        <!-- City quick-filters -->
        <div v-if="!isLoadingPlaces && cityOptions.length" class="chip-row">
          <button class="chip" :class="{ active: !cityFilter }" @click="setCityFilter('')">All Cities</button>
          <button
            v-for="c in cityOptions"
            :key="c"
            class="chip"
            :class="{ active: cityFilter === c }"
            @click="setCityFilter(c)"
          >{{ c }}</button>
        </div>

        <!-- Loading -->
        <div v-if="isLoadingPlaces" class="loading-row">
          <div class="loading-seal">
            <div class="seal-ring"></div>
            <div class="seal-ring-dashed"></div>
            <div class="seal-diamond"></div>
          </div>
          <p class="loading-text">Searching recommendations…</p>
        </div>

        <!-- Results -->
        <div v-else-if="filteredPlaces.length" class="place-results">
          <div
            v-for="post in filteredPlaces"
            :key="post.id"
            class="place-result"
            :class="{ 'place-result-travel': post.postType === 'travel_plan' }"
            @click="viewPost(post.id)"
          >
            <template v-if="post.postType === 'travel_plan'">
              <div class="place-result-tags">
                <span class="place-tag place-tag-travel">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  Traveling
                </span>
                <span class="place-tag place-tag-city">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
                  {{ post.city || 'Unknown City' }}
                </span>
              </div>
              <p class="place-result-name">{{ hasTravelDatePassed(post) ? `${post.fullName || 'A member'} was at ${post.city}` : `${post.fullName || 'A member'} is heading to ${post.city}` }}</p>
              <p v-if="post.travelDates" class="place-result-dates">{{ post.travelDates }}</p>
              <p v-if="post.text" class="place-result-text">{{ post.text }}</p>
              <div class="place-result-footer">
                <span class="place-result-author">Give them a recommendation →</span>
              </div>
            </template>
            <template v-else>
              <div class="place-result-tags">
                <span class="place-tag place-tag-city">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
                  {{ post.city || 'Unknown City' }}
                </span>
                <span class="place-tag place-tag-cat">{{ post.category || 'Recommendation' }}</span>
              </div>
              <p class="place-result-name">{{ post.businessName || 'Untitled Place' }}</p>
              <p v-if="post.text" class="place-result-text">{{ post.text }}</p>
              <div class="place-result-footer">
                <span class="place-result-author">Recommended by {{ post.fullName || 'a member' }}</span>
                <span v-if="post.arrangementCount > 0" class="place-result-arrangement">
                  {{ post.arrangementCount }} {{ post.arrangementCount === 1 ? 'member wants' : 'members want' }} this arranged
                </span>
              </div>
            </template>
          </div>
        </div>

        <!-- No results -->
        <div v-else class="empty-state">
          <div class="empty-seal">
            <div class="seal-ring"></div>
            <div class="seal-ring-dashed"></div>
            <div class="seal-diamond"></div>
          </div>
          <p class="empty-title">Nothing found</p>
          <p class="empty-sub">Try a different city, business, or category.</p>
        </div>
      </template>

      <!-- ══════════════ MEMBERS TAB ══════════════ -->
      <template v-else>

        <!-- Loading -->
        <div v-if="isLoadingMembers" class="loading-row">
          <div class="loading-seal">
            <div class="seal-ring"></div>
            <div class="seal-ring-dashed"></div>
            <div class="seal-diamond"></div>
          </div>
          <p class="loading-text">Curating the directory…</p>
        </div>

        <!-- Members grid -->
        <div v-else-if="filteredMembers.length" class="cards-grid">
          <div
            v-for="user in filteredMembers"
            :key="user.id"
            class="member-card"
            @click="viewUserDetails(user)"
          >
            <img
              v-if="user.image && user.image !== defaultImage"
              :src="user.image"
              :alt="user.fullName"
              class="card-photo"
            />
            <div v-else class="card-photo-placeholder">
              {{ (user.fullName || '?').charAt(0) }}
            </div>

            <div class="card-body">
              <p class="card-name">{{ user.fullName }}</p>
              <p class="card-role">{{ user.title || user.role || 'Member' }}</p>
            </div>

            <button class="card-btn" @click.stop="viewUserDetails(user)">
              <div class="card-btn-tick"></div>
              <span class="card-btn-label">View Profile</span>
              <div class="card-btn-tick"></div>
            </button>
          </div>
        </div>

        <!-- No results -->
        <div v-else class="empty-state">
          <div class="empty-seal">
            <div class="seal-ring"></div>
            <div class="seal-ring-dashed"></div>
            <div class="seal-diamond"></div>
          </div>
          <p class="empty-title">No members found</p>
          <p class="empty-sub">Try a different name or title.</p>
        </div>
      </template>

    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item active" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
        <div class="nav-dot"></div>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item" @click="$router.push('/member-settings')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { auth, db } from "@/firebase";

// Same rule as HomeView.vue / ExploreView.vue's Travel Intelligence
// filtering — a travel plan post disappears once the trip is over. A
// real end date/time is authoritative; without one, it's hidden a fixed
// number of days after posting instead of showing forever. Kept in sync
// with the other files' TRAVEL_PLAN_AUTO_EXPIRE_DAYS.
const TRAVEL_PLAN_AUTO_EXPIRE_DAYS = 7;

function hasTravelDatePassed(post) {
  const now = new Date();

  if (post.travelEndDate) {
    const end = post.travelEndDate?.toDate
      ? post.travelEndDate.toDate()
      : new Date(post.travelEndDate);
    return now > end;
  }

  const posted = post.timestamp?.toDate
    ? post.timestamp.toDate()
    : (post.timestamp ? new Date(post.timestamp) : null);
  if (!posted) return false;

  const ageMs = now - posted;
  return ageMs > TRAVEL_PLAN_AUTO_EXPIRE_DAYS * 24 * 60 * 60 * 1000;
}

function isTravelPlanExpired(post) {
  // Used for filtering/hiding only — a saved post's date can still have
  // passed grammatically (see hasTravelDatePassed) without being treated
  // as "expired" for visibility. This was previously missing the saved
  // check entirely, meaning a saved travel plan was being hidden from
  // search results the moment its date passed, defeating the point of
  // saving it.
  return hasTravelDatePassed(post) && post.saved !== true;
}

export default {
  name: "SearchView",

  data() {
    return {
      tab: 0,
      searchText: "",
      cityFilter: "",

      members: [],
      filteredMembers: [],
      isLoadingMembers: true,
      currentUserId: null,
      defaultImage: "@/assets/default-user.png",

      places: [],
      filteredPlaces: [],
      isLoadingPlaces: true,
    };
  },

  computed: {
    cityOptions() {
      const set = new Set(this.places.map((p) => p.city).filter(Boolean));
      return [...set].sort();
    },
  },

  async mounted() {
    await Promise.all([this.fetchMembers(), this.fetchPlaces()]);
    this._expireTickInterval = setInterval(() => { this.filterPlaces(); }, 15000);
  },

  beforeUnmount() {
    clearInterval(this._expireTickInterval);
  },

  methods: {
    hasTravelDatePassed(post) {
      return hasTravelDatePassed(post);
    },

    handleSearchInput() {
      this.filterMembers();
      this.filterPlaces();
    },

    clearSearch() {
      this.searchText = "";
      this.handleSearchInput();
    },

    setCityFilter(city) {
      this.cityFilter = this.cityFilter === city ? "" : city;
      this.filterPlaces();
    },

    async fetchMembers() {
      this.isLoadingMembers = true;
      try {
        this.currentUserId = auth.currentUser?.uid;
        const membersRef = collection(db, "user");
        const snapshot = await getDocs(membersRef);
        this.members = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        this.filterMembers();
      } catch (error) {
        console.error("Error fetching members:", error);
      } finally {
        this.isLoadingMembers = false;
      }
    },

    filterMembers() {
      const searchTextLower = this.searchText.toLowerCase();
      this.filteredMembers = this.members.filter((member) => {
        const isCurrentUser = member.id === this.currentUserId;
        const isAdmin = member.type && member.type.toLowerCase() === "admin";
        if (isCurrentUser || isAdmin) return false;
        return (
          (member.fullName && member.fullName.toLowerCase().includes(searchTextLower)) ||
          (member.title && member.title.toLowerCase().includes(searchTextLower))
        );
      });
    },

    async fetchPlaces() {
      this.isLoadingPlaces = true;
      try {
        const q = query(
          collection(db, "explore"),
          where("isPublic", "==", true),
          orderBy("timestamp", "desc")
        );
        const snapshot = await getDocs(q);
        this.places = snapshot.docs.map((doc) => {
          const data = doc.data();
          const arrangementInterest = Array.isArray(data.arrangementInterest) ? data.arrangementInterest : [];
          return {
            id: doc.id,
            ...data,
            arrangementCount: arrangementInterest.length,
          };
        });
        this.filterPlaces();
      } catch (error) {
        console.error("Error fetching recommendations:", error);
      } finally {
        this.isLoadingPlaces = false;
      }
    },

    filterPlaces() {
      const searchTextLower = this.searchText.toLowerCase();
      this.filteredPlaces = this.places.filter((post) => {
        if (post.postType === "travel_plan" && isTravelPlanExpired(post)) return false;
        if (this.cityFilter && post.city !== this.cityFilter) return false;
        if (!searchTextLower) return true;
        return (
          (post.city && post.city.toLowerCase().includes(searchTextLower)) ||
          (post.businessName && post.businessName.toLowerCase().includes(searchTextLower)) ||
          (post.category && post.category.toLowerCase().includes(searchTextLower)) ||
          (post.text && post.text.toLowerCase().includes(searchTextLower))
        );
      });
    },

    viewPost(postID) {
      this.$router.push({ name: "PostDetail", params: { postId: postID } });
    },

    viewUserDetails(user) {
      this.$router.push({
        name: "MemberDetail",
        params: { id: user.id, uid: user.uid },
      });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 1100px; margin: 0 auto; padding: 52px 48px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── SEARCH ── */
.search-wrap { display: flex; justify-content: center; margin-bottom: 40px; }
.search-box { position: relative; width: 100%; max-width: 600px; }
.search-icon { position: absolute; left: 0; top: 50%; transform: translateY(-50%); color: rgba(210,175,100,0.4); pointer-events: none; display: flex; }
.search-icon svg { width: 18px; height: 18px; }
.search-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid rgba(237,232,223,0.15); padding: 14px 36px 14px 32px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 16px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); transition: border-color 0.3s; letter-spacing: 0.3px; }
.search-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
.search-input:focus { border-bottom-color: rgba(210,175,100,0.4); }
.search-bar-line { position: absolute; bottom: 0; left: 0; height: 1px; width: 0; background: linear-gradient(90deg, rgba(210,175,100,0.8), rgba(210,175,100,0.2)); transition: width 0.5s cubic-bezier(0.25,0.46,0.45,0.94); pointer-events: none; }
.search-input:focus ~ .search-bar-line { width: 100%; }
.search-clear { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.25); display: flex; align-items: center; transition: color 0.2s; padding: 4px; }
.search-clear:hover { color: rgba(210,175,100,0.6); }
.search-clear svg { width: 14px; height: 14px; }

/* ── TABS ── */
.tab-row { display: flex; justify-content: center; border-bottom: 1px solid rgba(210,175,100,0.12); margin-bottom: 40px; }
.tab { font-size: 10px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(237,232,223,0.2); padding: 14px 28px; cursor: pointer; position: relative; background: none; border: none; font-family: 'Montserrat', sans-serif; transition: color 0.2s; }
.tab.active { color: rgba(210,175,100,0.8); }
.tab.active::after { content: ''; position: absolute; bottom: -1px; left: 0; right: 0; height: 1px; background: rgba(210,175,100,0.6); }
.tab:hover:not(.active) { color: rgba(237,232,223,0.45); }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 40px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 14px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.section-lede { font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.4); max-width: 620px; margin-bottom: 28px; }

/* ── CITY CHIPS ── */
.chip-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 28px; }
.chip { font-family: 'Montserrat', sans-serif; font-size: 9.5px; font-weight: 600; letter-spacing: 1.2px; text-transform: uppercase; color: rgba(237,232,223,0.35); background: transparent; border: 1px solid rgba(210,175,100,0.15); padding: 7px 13px; cursor: pointer; transition: color 0.2s, border-color 0.2s, background 0.2s; }
.chip:hover { border-color: rgba(210,175,100,0.4); color: rgba(237,232,223,0.6); }
.chip.active { color: rgba(210,175,100,0.9); border-color: rgba(210,175,100,0.6); background: rgba(210,175,100,0.08); }

/* ── LOADING ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 80px 0; }
.loading-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; }
.empty-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }

/* ── PLACE RESULTS ── */
.place-results { display: flex; flex-direction: column; gap: 16px; max-width: 760px; }
.place-result {
  border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02);
  padding: 22px 24px; cursor: pointer; position: relative; transition: border-color 0.2s;
}
.place-result::before, .place-result::after { content: ''; position: absolute; width: 12px; height: 12px; }
.place-result::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.place-result::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.place-result:hover { border-color: rgba(210,175,100,0.3); }
.place-result-tags { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.place-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 8.5px; font-weight: 600; letter-spacing: 1.8px; text-transform: uppercase; padding: 5px 10px; border: 1px solid rgba(210,175,100,0.25); color: rgba(210,175,100,0.7); }
.place-tag svg { width: 11px; height: 11px; }
.place-tag-city { color: rgba(210,175,100,0.75); border-color: rgba(210,175,100,0.3); }
.place-tag-cat { background: rgba(210,175,100,0.08); }
.place-tag-travel { color: rgba(150,190,220,0.8); border-color: rgba(140,180,210,0.3); background: rgba(140,180,210,0.1); }
.place-result-travel:hover { border-color: rgba(140,180,210,0.4); }
.place-result-dates { font-size: 10px; font-weight: 500; letter-spacing: 1px; color: rgba(150,190,220,0.6); text-transform: uppercase; margin-bottom: 8px; }
.place-result-name { font-family: 'Cormorant', serif; font-size: 24px; font-weight: 400; color: #edeae2; line-height: 1.15; margin-bottom: 8px; }
.place-result-text { font-size: 13px; font-weight: 300; line-height: 1.75; color: rgba(237,232,223,0.45); margin-bottom: 14px; }
.place-result-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding-top: 12px; border-top: 1px solid rgba(210,175,100,0.07); }
.place-result-author { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(237,232,223,0.25); }
.place-result-arrangement { font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(210,175,100,0.55); }

/* ── MEMBER CARDS ── */
.cards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.member-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); cursor: pointer; transition: border-color 0.3s; position: relative; overflow: hidden; }
.member-card::before, .member-card::after { content: ''; position: absolute; width: 14px; height: 14px; z-index: 1; }
.member-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.member-card::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.member-card:hover { border-color: rgba(210,175,100,0.3); }
.member-card:hover .card-photo { filter: brightness(1.06); }

.card-photo { width: 100%; height: 260px; object-fit: cover; object-position: top center; display: block; filter: brightness(0.93); transition: filter 0.4s; }
.card-photo-placeholder { width: 100%; height: 260px; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 64px; font-weight: 300; color: rgba(210,175,100,0.2); }
.card-body { padding: 22px 24px 0; }
.card-name { font-family: 'Cormorant', serif; font-size: 22px; font-weight: 400; color: #edeae2; letter-spacing: -0.2px; margin-bottom: 6px; line-height: 1.1; }
.card-role { font-size: 10px; font-weight: 500; letter-spacing: 2.5px; color: rgba(210,175,100,0.45); text-transform: uppercase; margin-bottom: 20px; }

.card-btn { width: 100%; border: none; border-top: 1px solid rgba(210,175,100,0.1); background: transparent; padding: 17px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 12px; position: relative; overflow: hidden; transition: border-top-color 0.3s; }
.card-btn::before { content: ''; position: absolute; inset: 0; background: rgba(210,175,100,0.05); transform: scaleX(0); transform-origin: left; transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94); }
.member-card:hover .card-btn::before { transform: scaleX(1); }
.member-card:hover .card-btn { border-top-color: rgba(210,175,100,0.2); }
.card-btn-tick { width: 18px; height: 1px; background: rgba(210,175,100,0.3); transition: width 0.3s; flex-shrink: 0; }
.member-card:hover .card-btn-tick { width: 24px; }
.card-btn-label { font-size: 10px; font-weight: 600; letter-spacing: 3.5px; color: rgba(210,175,100,0.7); text-transform: uppercase; }

/* ── EMPTY STATE ── */
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 80px 0; border: 1px solid rgba(210,175,100,0.08); }
.empty-title { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300; color: rgba(237,232,223,0.3); margin-bottom: 10px; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── RESPONSIVE ── */
@media (max-width: 900px) { .cards-grid { grid-template-columns: repeat(2, 1fr); } .page { padding: 40px 28px 120px; } }
@media (max-width: 600px) {
  .cards-grid { grid-template-columns: 1fr; }
  .page { padding: 32px 20px 120px; }
  .frame, .frame-inner { inset: 12px; }
  .section-title { font-size: 32px; }
  .place-result { padding: 18px; }
  .place-result-name { font-size: 20px; }
}
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>